import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { CheckIcon, Database, FileText, Lock, ShieldCheck, Users2 } from "lucide-react";
import { PricesResponse } from "@/pages/api/prices";
import { MarketingLink } from "@/components/OptionalLink";
import { capitalize, cn } from "@/lib/utils";
import plans from "@/plans.json";
import { load } from "recaptcha-v3";
import logger from "@/lib/logger";
import { GitHubIcon } from "@/components/GitHubIcon";

const checkRecaptchaUser = async (): Promise<string | null> => {
  const key = process.env.NEXT_PUBLIC_RECAPTCHA_KEY;
  if (!key) {
    logger.warn("Missing NEXT_PUBLIC_RECAPTCHA_KEY");
    return null;
  }
  try {
    const recaptcha = await load(key, {
      autoHideBadge: true
    });
    logger.info("Executing recaptcha for subscribe");
    const token = await recaptcha.execute("subscribe");
    logger.info("Recaptcha token obtained");
    return token;
  } catch (error) {
    logger.warn("Failed to execute recaptcha", error);
    return null;
  }
};

const formatPrice = (amount: number | null | undefined, currency: string = "usd"): string => {
  if (!amount) {
    return "";
  }
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  return formatter.format(amount / 100);
};

export const createPricingList = (prices: PricesResponse[]) => {
  let priceIndex = 0;

  return Object.entries(plans)
    .filter(([, plan]) => plan.active)
    .map(([name, plan]) => {
      const isFree = name === "free";
      const { title, description, features, tag, custom } = plan;
      const descriptionYearly =
        "descriptionYearly" in plan &&
        typeof (plan as { descriptionYearly?: string }).descriptionYearly === "string"
          ? (plan as { descriptionYearly: string }).descriptionYearly
          : undefined;
      const priceData = !isFree && prices ? prices[priceIndex] : undefined;
      const monthlyPrice = priceData?.monthly.unit_amount;
      const yearlyPrice = priceData?.yearly.unit_amount;

      if (!isFree && !custom) {
        priceIndex++;
      }

      return {
        id: name,
        title,
        custom,
        monthlyPrice: isFree
          ? "$0/month"
          : monthlyPrice && priceData?.monthly.currency
            ? `${formatPrice(monthlyPrice, priceData?.monthly.currency)}/month`
            : undefined,
        yearlyMonthly: isFree
          ? undefined
          : yearlyPrice && monthlyPrice
            ? `${formatPrice(
                Math.round((yearlyPrice * 100) / 12) / 100,
                priceData?.yearly.currency
              )}/month`
            : undefined,
        description,
        descriptionYearly: descriptionYearly ?? undefined,
        tag,
        benefitList: features,
        free: isFree
      };
    });
};

type PricingProps = ReturnType<typeof createPricingList>[0];

export const Pricing = ({ pricing }: { pricing: PricingProps[] }) => {
  const [yearly, setYearly] = useState(true);
  const enterprise = pricing.find(price => price.custom);

  return (
    <section id="pricing" className="container space-y-8 md:space-y-16">
      <div className="text-center space-y-4">
        <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
          Start for <em className="text-primary">free</em> with 1 app and 1 server
        </h2>

        <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-balance">
          Upgrade your plan to get more servers and features.
        </p>

        <div className="flex items-center justify-center gap-3 pt-4">
          <span
            className={cn(
              "text-sm font-medium",
              yearly ? "text-foreground" : "text-muted-foreground"
            )}
          >
            Monthly
          </span>

          <Switch checked={yearly} onCheckedChange={setYearly} />

          <span
            className={cn(
              "text-sm font-medium",
              yearly ? "text-foreground" : "text-muted-foreground"
            )}
          >
            Yearly
          </span>

          <Badge
            variant="secondary"
            className={cn("ml-2", !yearly && "invisible")}
            aria-hidden={!yearly}
          >
            Save up to 20%
          </Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
        {pricing
          .filter(price => !price.custom)
          .map(price => (
            <PricingCard
              key={price.id}
              price={price}
              yearly={yearly}
              className={
                price.id.startsWith("pro:") ? "outline-primary/80 outline-3" : undefined
              }
            />
          ))}
      </div>

      {enterprise && <EnterprisePricingCard price={enterprise} />}

      <div className="text-center">
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-balance prose">
          <b>NB:</b> You pay for your servers separately to your cloud provider.
          <br />
          Our offer comes with a money-back guarantee. If you&apos;re not satisfied, we&apos;ll
          refund your money.
        </p>
      </div>

      <div className="mt-16 text-center">
        <div className="text-center space-y-4 mb-16 pt-8">
          <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
            <em className="text-primary">All</em> Plans Include
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-balance">
            Every plan comes fully featured to deploy modern apps to your own server.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-10 max-w-3xl mx-auto">
          <div className="flex flex-col items-center gap-2">
            <Lock className="w-8 h-8 text-primary" />
            <span className="font-medium">Automatic HTTPS</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Database className="w-8 h-8 text-primary" />
            <span className="font-medium">Backups</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <GitHubIcon className="w-8 h-8 text-primary" />
            <span className="font-medium">GitHub Integration</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-primary" />
            <span className="font-medium">Reverse proxy</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Users2 className="w-8 h-8 text-primary" />
            <span className="font-medium">Support</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <FileText className="w-8 h-8 text-primary" />
            <span className="font-medium">Templates</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PricingCard = ({
  className,
  price,
  yearly
}: {
  className?: string;
  price: PricingProps;
  yearly: boolean;
}) => {
  const displayPrice = price.custom
    ? "Call us"
    : yearly && price.yearlyMonthly
      ? price.yearlyMonthly
      : price.monthlyPrice;

  return (
    <Card
      key={price.title}
      className={cn("drop-shadow-xl shadow-black/10 dark:shadow-white/10", className)}
    >
      <CardHeader>
        <CardTitle className="text-xl flex item-center justify-between">
          {price.title}
          {price.tag ? (
            <Badge variant="secondary" className="text-sm">
              {price.tag}
            </Badge>
          ) : null}
        </CardTitle>

        <div className="text-left py-2 flex items-baseline gap-2">
          <span className="text-xl font-bold">{displayPrice}</span>
          {yearly && !price.custom && !price.free && (
            <Badge variant="outline" className="text-xs">
              Annual
            </Badge>
          )}
        </div>

        <CardDescription className="text-left text-sm text-secondary-foreground">
          {yearly && price.descriptionYearly ? price.descriptionYearly : price.description}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {price.custom ? (
          <Button className="w-full" asChild>
            <MarketingLink href="mailto:hello@dollardeploy.com">Call us</MarketingLink>
          </Button>
        ) : price.free ? (
          <Button className="w-full" asChild>
            <MarketingLink
              href="/auth/signIn?callbackUrl=/dashboard"
              className={"plausible-event-name=Pricing" + capitalize(price.id)}
            >
              Get started for free
            </MarketingLink>
          </Button>
        ) : (
          <Button className="w-full" asChild>
            <MarketingLink
              href={"/api/subscribe/" + (yearly ? "yearly:" : "monthly:") + price.id}
              onClick={async e => {
                if (process.env.NEXT_PUBLIC_RECAPTCHA_KEY) {
                  e.preventDefault();
                  const token = await checkRecaptchaUser();
                  if (token) {
                    const url = new URL(
                      "/api/subscribe/" + (yearly ? "yearly:" : "monthly:") + price.id,
                      window.location.origin
                    );
                    url.searchParams.set("token", token);
                    window.location.href = url.toString();
                  } else {
                    logger.warn("Failed to get reCAPTCHA token, blocking subscription");
                  }
                }
              }}
              className={"plausible-event-name=Pricing" + capitalize(price.id)}
            >
              {process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS
                ? `Start ${process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS}-Day Free Trial`
                : "Subscribe now"}
            </MarketingLink>
          </Button>
        )}
      </CardContent>

      <CardFooter className="flex">
        <div className="space-y-4 ml-1">
          {price.benefitList?.map((benefit: string) => (
            <span
              key={benefit}
              className="flex items-center text-secondary-foreground/80 gap-2 text-sm"
            >
              <CheckIcon className="w-6 h-6 stroke-1 border border-secondary-foreground/80 rounded-full p-1" />
              <span className="font-sm">{benefit}</span>
            </span>
          ))}
        </div>
      </CardFooter>
    </Card>
  );
};

export const EnterprisePricingCard = ({
  className,
  price
}: {
  className?: string;
  price: PricingProps;
}) => {
  return (
    <Card
      className={cn(
        "drop-shadow-xl shadow-black/10 dark:shadow-white/10 max-w-5xl mx-auto bg-primary/5 border border-primary/20",
        className
      )}
    >
      <CardHeader className="items-center text-center">
        <CardTitle className="text-2xl text-center">
          Looking for <span className="text-primary">enterprise</span> solution?
        </CardTitle>
      </CardHeader>

      <div className="flex flex-col lg:grid lg:grid-cols-2 lg:items-center">
        <div>
          <CardHeader>
            <CardTitle className="text-xl flex item-center justify-between">
              {price.title}
              {price.tag ? (
                <Badge variant="secondary" className="text-sm bg-primary/10">
                  {price.tag}
                </Badge>
              ) : null}
            </CardTitle>
            <div className="text-left py-2"></div>

            <CardDescription className="text-left text-sm text-secondary-foreground">
              {price.description}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Button className="w-full my-4 lg:w-fit items-center justify-center" asChild>
              <MarketingLink
                href="mailto:hello@dollardeploy.com"
                aria-label="Contact us about enterprise plans"
                className="plausible-event-name=PricingEnterprise"
              >
                Talk to sales
              </MarketingLink>
            </Button>
          </CardContent>
        </div>

        <CardFooter className="flex items-start border-border md:items-center md:border-l">
          <div className="space-y-4 ml-1">
            {price.benefitList?.map((benefit: string) => (
              <span
                key={benefit}
                className="flex items-center text-secondary-foreground/80 gap-2 text-sm"
              >
                <CheckIcon className="w-6 h-6 stroke-1 border border-secondary-foreground/80 rounded-full p-1 bg-primary text-primary-foreground" />
                <span className="font-sm">{benefit}</span>
              </span>
            ))}
          </div>
        </CardFooter>
      </div>
    </Card>
  );
};
